'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';

interface BenefitCard {
  emoji: string;
  title: string;
  description: string;
}

const benefits: BenefitCard[] = [
  {
    emoji: '🏗️',
    title: 'Architectural Freedom',
    description: 'Custom angles, complex geometries, and high-rise capability up to 20+ floors.',
  },
  {
    emoji: '⚡',
    title: 'Speed & Efficiency',
    description: 'Eliminate formwork delays. Rapid on-site assembly with precision engineering.',
  },
  {
    emoji: '🌱',
    title: 'Sustainability',
    description: 'Circular economy approach with disassembly and adaptive reuse capability.',
  },
  {
    emoji: '💰',
    title: 'Cost Optimization',
    description: 'Reduced capital requirements, lower logistics costs, local labor utilization.',
  },
];

export default function BenefitsSection() {
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="section-padding" ref={sectionRef}>
      <div className="container mx-auto container-padding">
        {/* Header */}
        <div className="mb-12 lg:mb-16 text-center">
          <h2 className="text-section lg:text-section-lg font-heading font-bold text-[hsl(0,0%,9%)] mb-4 mx-auto max-w-3xl">
            Platform Benefits
          </h2>
          <p className="text-lg text-[hsl(0,0%,45%)] max-w-2xl mx-auto">
            Why industry leaders choose{' '}
            <span className="font-bold text-nexsys-brand">NeXSYS</span>{' '}
            for their construction projects.
          </p>
        </div>

        {/* Cards grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {benefits.map((benefit, index) => (
            <div
              key={benefit.title}
              className="bg-white rounded-xl p-6 shadow-card hover:shadow-card-hover transition-all duration-300 hover:-translate-y-1 border border-[hsl(0,0%,83%)] text-center"
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(20px)',
                transition: `opacity 0.5s ease ${index * 0.1}s, transform 0.5s ease ${index * 0.1}s`,
              }}
            >
              <div className="text-4xl mb-4">{benefit.emoji}</div>
              <h3 className="font-heading font-bold text-lg text-[hsl(0,0%,9%)] mb-2">
                {benefit.title}
              </h3>
              <p className="text-[hsl(0,0%,45%)] text-sm">{benefit.description}</p>
            </div>
          ))}
        </div>

        {/* View All Benefits CTA */}
        <div className="text-center mt-12">
          <Link
            href="/benefits"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap font-semibold border-2 border-[hsl(161,93%,30%)] bg-transparent text-[hsl(161,93%,30%)] hover:bg-[hsl(161,93%,30%)] hover:text-white h-12 rounded-lg px-8 text-base transition-all duration-300"
          >
            View All Benefits
          </Link>
        </div>
      </div>
    </section>
  );
}
